import React, { useState, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { X, Twitter, Loader2, Check, AlertCircle, LogOut } from 'lucide-react';
import { 
  isTwitterConnected, 
  initiateTwitterAuth, 
  disconnectTwitter,
  getTwitterUsername,
  getTwitterUserTier,
  TwitterTokens
} from '../utils/twitter';

interface TwitterAuthOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onConnectionChange?: (connected: boolean, username?: string) => void;
}

type AuthStep = 'idle' | 'connecting' | 'success' | 'error';

export const TwitterAuthOverlay: React.FC<TwitterAuthOverlayProps> = ({
  isOpen,
  onClose,
  onConnectionChange,
}) => {
  const [step, setStep] = useState<AuthStep>('idle');
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [userTier, setUserTier] = useState<'basic' | 'premium'>('basic');
  const titleId = useId();

  // Check connection status on mount and when opening
  useEffect(() => {
    if (isOpen) {
      const isConnected = isTwitterConnected();
      setConnected(isConnected);
      if (isConnected) {
        setUsername(getTwitterUsername());
        setUserTier(getTwitterUserTier());
      }
      setStep('idle');
      setError(null);
    }
  }, [isOpen]);

  const handleConnect = async () => {
    setStep('connecting');
    setError(null);
    
    try {
      const tokens: TwitterTokens = await initiateTwitterAuth();
      setConnected(true);
      setUsername(tokens.username || null);
      setUserTier(getTwitterUserTier());
      setStep('success');
      onConnectionChange?.(true, tokens.username);
      
      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to connect to Twitter');
      setStep('error');
    }
  };

  const handleDisconnect = () => {
    disconnectTwitter();
    setConnected(false);
    setUsername(null);
    setUserTier('basic');
    onConnectionChange?.(false);
  };

  if (!isOpen) return null;

  const overlay = (
    // Above the pages as well as the canvas: Settings opens this dialog, and Settings is a page
    // lying over the canvas at z-200. On the scrim's own layer (100) it would open underneath.
    <div className="ns-modal-scrim z-[400]">
      {/* Backdrop: the scrim above draws it, this takes the click outside the sheet */}
      <div
        className="absolute inset-0"
        onClick={onClose}
      />

      {/* Modal */}
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="ns-modal-sheet ns-modal-sheet-flush">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 pl-6 pr-5 pt-5 pb-4 border-b border-cream-300">
          <div className="flex items-center gap-3 min-w-0">
            {/* The mark of X stays in its blue, from the palette's sky tone */}
            <span data-tone="sky" className="ns-mark">
              <Twitter />
            </span>
            <div className="min-w-0">
              <h2 id={titleId} className="ns-t-dialog">Twitter / X</h2>
              <p className="text-sm text-ink-soft">
                {connected ? 'Connected' : 'Connect your account'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="ns-icon-btn" aria-label="Close">
            <X />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Already Connected State */}
          {connected && step === 'idle' && (
            <>
              <div className="ns-empty">
                <span data-tone="ok" className="ns-mark ns-mark-lg">
                  <Check />
                </span>
                <h3 className="ns-empty-title">Connected!</h3>
                <p className="ns-empty-text">@{username || 'Unknown'}</p>

                {/* User Tier Badge */}
                <span className={`ns-chip ns-chip-sm mt-1 ${userTier === 'premium' ? 'ns-chip-lavender' : ''}`}>
                  {userTier === 'premium' ? 'Premium' : 'Basic'} account
                </span>
              </div>

              <div className="flex flex-wrap justify-end gap-3">
                <button
                  onClick={handleDisconnect}
                  className="ns-btn ns-btn-sm ns-btn-ghost"
                >
                  <LogOut size={14} />
                  Disconnect account
                </button>
                <button
                  onClick={onClose}
                  className="ns-btn ns-btn-sm ns-btn-primary"
                >
                  Done
                </button>
              </div>
            </>
          )}

          {/* Not Connected State */}
          {!connected && step === 'idle' && (
            <div className="ns-empty">
              <span data-tone="sky" className="ns-mark ns-mark-lg">
                <Twitter />
              </span>
              <h3 className="ns-empty-title">Connect your account</h3>
              <p className="ns-empty-text">
                Link your Twitter/X account to share your creations directly from the canvas.
              </p>

              <button
                onClick={handleConnect}
                className="ns-btn ns-btn-sm ns-btn-primary mt-3"
              >
                <Twitter size={14} />
                Connect Twitter
              </button>

              <p className="ns-t-meta mt-1">
                We'll open a popup to authenticate with Twitter
              </p>
            </div>
          )}

          {/* Connecting State */}
          {step === 'connecting' && (
            <div className="ns-empty">
              <span data-tone="sky" className="ns-mark ns-mark-lg">
                <Loader2 className="animate-spin" />
              </span>
              <h3 className="ns-empty-title">Connecting...</h3>
              <p className="ns-empty-text">
                Please complete the authorization in the popup window.
              </p>
            </div>
          )}

          {/* Success State */}
          {step === 'success' && (
            <div className="ns-empty">
              <span data-tone="ok" className="ns-mark ns-mark-lg">
                <Check />
              </span>
              <h3 className="ns-empty-title">Connected!</h3>
              <p className="ns-empty-text">
                Your Twitter account has been linked successfully.
              </p>
            </div>
          )}

          {/* Error State */}
          {step === 'error' && (
            <>
              <div className="ns-empty">
                <span data-tone="danger" className="ns-mark ns-mark-lg">
                  <AlertCircle />
                </span>
                <h3 className="ns-empty-title">Connection failed</h3>
                <p className="ns-empty-text text-danger-deep break-words">{error}</p>
              </div>

              <div className="flex flex-wrap justify-end gap-3">
                <button
                  onClick={onClose}
                  className="ns-btn ns-btn-sm ns-btn-soft"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConnect}
                  className="ns-btn ns-btn-sm ns-btn-primary"
                >
                  Try again
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Use portal to render at document body level
  return createPortal(overlay, document.body);
};

export default TwitterAuthOverlay;
